import { styled } from '@mui/material';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import accountService from 'services/account';
import batchService from 'services/batch';
import ltiService from 'services/lti';
import { Box, Button, Icon, IconButton, ResponsiveDialog, Stack, Text } from 'shell/components';

const LoadingStack = styled(Stack)(({ $loading }) => {
    return {
        opacity: $loading ? 1 : 0,
    };
});

const LoadingLeftBox = styled(Box)(() => {
    return {
        fontSize: 20,
        animation: 'loading-left 1s ease-out infinite',
        '@keyframes loading-left': {
            '7.5%': {
                transform: 'translate(50%, -15%)',
            },
            '15%': {
                transform: 'translate(100%, -20%)',
            },
            '22.5%': {
                transform: 'translate(175%, -15%)',
            },
            '30%, 100%': {
                transform: 'translate(200%, 0)',
            },
        },
    };
});

const LoadingRightStack = styled(Stack)(() => {
    return {
        fontSize: 20,
        animation: 'loading-right 1s ease-in-out infinite',
        '@keyframes loading-right': {
            '30%, 100%': {
                transform: 'translateX(-50%)',
            },
        },
    };
});

const LoadingIndicator = ({ loading }) => {
    return (
        <LoadingStack aria-hidden={!loading} direction={'row'} $loading={loading} role={'progressbar'}>
            <LoadingLeftBox>.</LoadingLeftBox>
            <LoadingRightStack direction={'row'}>..</LoadingRightStack>
        </LoadingStack>
    );
};

const RotatingStack = styled(Stack)(({ loading }) => {
    return loading
        ? {
              animation: 'rotation 2s infinite linear',
              '@keyframes rotation': {
                  from: {
                      transform: 'rotate(0deg)',
                  },
                  to: {
                      transform: 'rotate(359deg)',
                  },
              },
          }
        : {};
});

const CustomCirculatingIcon = ({ loading }) => {
    return (
        <RotatingStack loading={loading}>
            <Icon kind={'resync'} />
        </RotatingStack>
    );
};

const SyncRosterDialog = ({ device, open, isGroupSyncing, setSyncGroupDialog, onClose }) => {
    const { message } = useSnackbar();
    const { auth } = useAuth();
    const { accountUsers, addAccountUser, removeAccountUser } = useApp();

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('get-roster');
    const [data, setData] = useState({});

    const handleClose = () => {
        if (loading) return;
        return onClose();
    };

    const getLatestRoster = async () => {
        const getLTIUsers = await ltiService.listLTIUsers({ course_id: auth.course_id });
        const filterInstructors = getLTIUsers.filter((user) => user.role === 'student' && user.email);

        const mergeAccountAndUsers = accountUsers
            .filter((user) => user.active && user.role === 'student' && user.email)
            .map((user) => {
                return {
                    ...user,
                    id: user.owner_account_id,
                    user_id: user.id,
                    username: user.username,
                    email: user.username,
                };
            })
            .filter((user) => user);
        const findDeletion = _.differenceBy(mergeAccountAndUsers, filterInstructors, 'email');
        const findAddition = _.differenceBy(filterInstructors, mergeAccountAndUsers, 'email');
        let toDeleteArray = [];
        let toAddArray = [];

        findDeletion.forEach((member) => {
            const toDeleteUser = mergeAccountAndUsers.find(
                (user) => user.username === member.email && user.active && user.role && user.role === 'student'
            );
            if (toDeleteUser) {
                toDeleteArray = [...toDeleteArray, toDeleteUser];
            }
        });
        findAddition.forEach((member) => {
            const toAddUser = mergeAccountAndUsers.find(
                (user) => user.username === member.email && user.active && user.role && user.role === 'student'
            );
            if (!toAddUser) {
                const newMember = {
                    ...member,
                    active: true,
                    id: uuid(),
                    user_id: member.id,
                    owner_account_id: member.owner_account_id,
                };
                toAddArray = [...toAddArray, newMember];
            }
        });
        return {
            toDeleteArray,
            toAddArray,
        };
    };

    const getRoster = async () => {
        const rosterData = await getLatestRoster();
        if (rosterData.toAddArray?.length === 0 && rosterData.toDeleteArray?.length === 0) {
            handleClose();
            if (isGroupSyncing) return setSyncGroupDialog({ open: true });
            return message.success('Your roster is up to date');
        }
        setData(rosterData);
        setStep('sync-roster');
    };

    const syncRoster = async ({ toDeleteArray, toAddArray }) => {
        const removeUser = async (accountUser) => {
            await accountService.deleteAccount(accountUser.id);
            removeAccountUser(accountUser);
        };

        const activateUser = async (accountUser) => {
            await batchService.batchSendInvitations({
                data: {
                    is_lti: true,
                    root_url: window.location.origin,
                    course_id: auth.course_id,
                    type: 'student',
                    users: [{ ...accountUser, user_id: accountUser?.user_id }],
                },
            });
            addAccountUser(accountUser);
        };
        setLoading(true);
        if (toDeleteArray?.length > 0) {
            await Promise.all(
                toDeleteArray.map(async (user) => {
                    return removeUser(user);
                })
            );
        }
        if (toAddArray?.length > 0) {
            await Promise.all(
                toAddArray.map(async (user) => {
                    return activateUser(user);
                })
            );
        }
        setLoading(false);
        return handleClose();
    };

    const handleSyncRoster = async () => {
        await syncRoster(data);
        if (isGroupSyncing) return setSyncGroupDialog({ open: true });
    };

    const renderGettingRoster = () => {
        return (
            <Stack alignItems={'center'} justifyContent={'center'} spacing={10}>
                <Stack sx={{ width: 620 }}>
                    <Stack alignItems={'center'} direction={'column'} justifyContent={'center'} spacing={4}>
                        <Icon kind={'clock'} sx={{ fontSize: 24 }} />
                        <Stack alignItems={'center'} direction={'row'}>
                            <Text variant={'h6'}>Please wait while we check your member roster</Text>
                            <LoadingIndicator loading={true} loadingGrow={false} />
                        </Stack>
                    </Stack>
                    <Stack alignItems={'center'} justifyContent={'center'}>
                        <TextStack device={device}>
                            <Text variant={'p2'} textAlign={'center'}>
                                This process may vary in speed depending on class size, so please be patient while we
                                complete the verification.
                            </Text>
                        </TextStack>
                    </Stack>
                </Stack>
                <Stack direction={'row'} justifyContent={'center'}>
                    <Button color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        );
    };

    const renderConfirmSyncRoster = () => {
        return (
            <Stack alignItems={'center'} justifyContent={'space-between'} spacing={12}>
                <Stack>
                    <HeaderContainer alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={3}>
                            <CustomIcon kind={'error'} />
                            <Text variant={'h6'}>We noticed that your roster is out of date</Text>
                        </Stack>
                        <IconButton kind={'exit'} size={'medium'} variant={'square'} onClick={handleClose} />
                    </HeaderContainer>
                    <TextStack alignItems={'center'} direction={'column'} justifyContent={'center'} spacing={5}>
                        <Stack sx={{ width: 420 }}>
                            <Text variant={'p2'}>
                                Please sync your member roster below to ensure that you are using the most current
                                version.
                            </Text>
                        </Stack>
                        <Stack>
                            <ul>
                                <Text>({data?.toAddArray?.length || 0}) students will be added.</Text>
                            </ul>
                            <ul>
                                <Text>({data?.toDeleteArray?.length || 0}) students will be removed.</Text>
                            </ul>
                        </Stack>
                    </TextStack>
                </Stack>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'center'} spacing={4}>
                    <Button
                        disabled={loading}
                        onClick={handleSyncRoster}
                        startIcon={<CustomCirculatingIcon loading={loading} kind={'resync'} />}
                    >
                        Sync Roster
                    </Button>
                </Stack>
            </Stack>
        );
    };

    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setStep('get-roster');
            }, 100);
        }
        if (open) {
            (async () => {
                await getRoster();
            })();
        }
    }, [open]);

    return (
        <ResponsiveDialog open={open} onClose={handleClose} closable={false}>
            {step === 'get-roster' ? renderGettingRoster() : renderConfirmSyncRoster()}
        </ResponsiveDialog>
    );
};

export default SyncRosterDialog;

const HeaderContainer = styled(Stack)(({ theme }) => {
    return {
        marginBottom: theme.spacing(2),
    };
});

const TextStack = styled(Stack)(({ device, theme }) => {
    return {
        margin: theme.spacing(4, 0),
        width: '100%',
        ...(device === 'mobile' && {
            width: '100%',
        }),
    };
});

export const CustomIcon = styled(Icon)(({ dark, theme }) => {
    return {
        borderRadius: '50%',
        padding: '0.3125rem',
        fontSize: '1.5rem',
        background: dark ? 'transparent' : theme.palette.ensightful.primary[500],
        color: dark ? theme.palette.ensightful.neutral[50] : theme.palette.ensightful.shades[0],
    };
});
